import { Tag } from '../store/slices/tagsSlice';

export type ActivityType =
  | 'Flight'
  | 'Hotel'
  | 'Activity'
  | 'CarRental'
  | 'Concert'
  | 'Theatre'
  | 'Meeting'
  | 'Shopping'
  | 'Restaurant'
  | 'Tour'
  | 'Parking'
  | 'Directions'
  | 'BorderCrossing'
  | 'Ferry'
  | 'Cruise'
  | 'Rail'
  | 'Transportation';

export const ACTIVITY_TYPE_OPTIONS: ActivityType[] = [
  'Flight',
  'Hotel',
  'Activity',
  'CarRental',
  'Concert',
  'Theatre',
  'Meeting',
  'Shopping',
  'Restaurant',
  'Tour',
  'Parking',
  'Directions',
  'BorderCrossing',
  'Ferry',
  'Cruise',
  'Rail',
  'Transportation',
];

export interface TravelContactData {
  name: string;
  link?: string | null;
  email?: string | null;
  phone_number?: string | null;
}

export interface TravelContactResponse extends TravelContactData {
  id: number;
  added_by_type: 'user' | 'system';
}

interface BaseActivityData {
  amount_spend?: number;
  belonged_travel_contact?: TravelContactData;
  link?: string;
  email?: string;
  phone_number?: string;
  notes?: string;
  travel_contact?: TravelContactData;
}

interface AddressData {
  name: string;
  latitude: number;
  longitude: number;
}

export interface FlightData extends BaseActivityData {
  flight_from: string;
  flight_to: string;
  flight_from_airport_name?: string;
  flight_to_airport_name?: string;
  departure_date_time: string;
  arrival_date_time: string;
  airline?: string;
  airline_iata_code?: string;
  flight_number?: string;
  seat?: string;
}

export interface HotelData extends BaseActivityData {
  name: string;
  check_in_date_time: string;
  check_out_date_time: string;
  address?: AddressData;
  booking?: string;
}

export interface ActivityData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  address?: AddressData;
  booking?: string;
  place?: string;
}

export interface CarRentalData extends BaseActivityData {
  name?: string;
  start_date_time: string;
  end_date_time: string;
  pick_up_address?: AddressData;
  drop_off_address?: AddressData;
  transport_name?: string;
  address?: AddressData;
  booking?: string;
}

export interface ConcertData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  seat?: string;
  address?: AddressData;
  booking?: string;
}

export interface TheatreData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  seat?: string;
  address?: AddressData;
  booking?: string;
}

export interface MeetingData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  address?: AddressData;
  booking?: string;
}

export interface ShoppingData extends BaseActivityData {
  name?: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  address?: AddressData;
  booking?: string;
}

export interface RestaurantData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  address?: AddressData;
  booking?: string;
}

export interface TourData extends BaseActivityData {
  name: string;
  start_date_time: string;
  end_date_time: string;
  place?: string;
  address?: AddressData;
  booking?: string;
}

export interface ParkingData extends BaseActivityData {
  start_date_time: string;
  end_date_time: string;
  place: string;
  address?: AddressData;
  booking?: string;
}

export interface DirectionsData extends BaseActivityData {
  start_date_time: string;
  end_date_time: string;
  place: string;
  address?: AddressData;
  booking?: string;
}

export interface BorderCrossingData extends BaseActivityData {
  start_date_time: string;
  country_from: string;
  country_to: string;
  border_checkpoint?: string;
  address?: AddressData;
}

export interface FerryData extends BaseActivityData {
  name: string;
  departure_date_time: string;
  arrival_date_time: string;
  carrier_name?: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
  vehicle_description?: string;
  number_of_passengers?: number;
  seat?: string;
  booking?: string;
}

export interface CruiseData extends BaseActivityData {
  name: string;
  departure_date_time: string;
  arrival_date_time: string;
  transport_name?: string;
  location_from_name?: string;
  location_to_name?: string;
  starting_port_address?: AddressData;
  ending_port_address?: AddressData;
  cabin_type?: string;
  cabin?: string;
  booking?: string;
}

export interface RailData extends BaseActivityData {
  name: string;
  departure_date_time: string;
  arrival_date_time: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
  train_type?: string;
  train?: string;
  seat?: string;
  seat_type?: string;
  booking?: string;
}

export interface TransportationData extends BaseActivityData {
  vehicle_type: string;
  departure_date_time: string;
  arrival_date_time: string;
  carrier_name?: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
  number_of_passengers?: number;
  booking?: string;
}

export type ActivityDataUnion =
  | FlightData
  | HotelData
  | ActivityData
  | CarRentalData
  | ConcertData
  | TheatreData
  | MeetingData
  | ShoppingData
  | RestaurantData
  | TourData
  | ParkingData
  | DirectionsData
  | BorderCrossingData
  | FerryData
  | CruiseData
  | RailData
  | TransportationData;

export interface CreateActivityFormData {
  activityType: ActivityType;
  name: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  date: string;
  time: string; 

  flightFrom: string;
  flightTo: string;
  place: string;
  country_from: string;
  country_to: string;

  airline: string;
  flightNumber: string;
  ticketBookingNumber: string;
  seatNumber: string;
  carrierName: string;
  bookingNumber: string;
  departureLocation: string;
  departureAddress: string;
  arrivalLocation: string;
  arrivalAddress: string;
  vehicleDescription: string;
  numberOfPassengers: number | '';
  vehicleType: string;
  pickupAddress: string;
  sameAsPickupAddress: boolean;
  dropOffAddress: string;
  officeAddress: string;
  address: string;
  shipName: string;
  startingPortName: string;
  startingPortAddress: string;
  sameAsStartingPort: boolean;
  endingPortName: string;
  endingPortAddress: string;
  cabinType: string;
  cabinNumber: string;
  departureStation: string;
  arrivalStation: string;
  trainType: string;
  trainNumber: string;
  class: string;
  borderCheckpoint: string;
  parkingSpaceNumber: string;
  phone: string;
  email: string;
  link: string;
  notes: string;
  amountSpent: number | '';

  documentName: string;
  documentTags: Tag[];
  attachments: File[];

  address_details?: google.maps.places.PlaceResult;
  pickupAddress_details?: google.maps.places.PlaceResult;
  dropOffAddress_details?: google.maps.places.PlaceResult;
  officeAddress_details?: google.maps.places.PlaceResult;
  departureAddress_details?: google.maps.places.PlaceResult;
  arrivalAddress_details?: google.maps.places.PlaceResult;
  startingPortAddress_details?: google.maps.places.PlaceResult;
  endingPortAddress_details?: google.maps.places.PlaceResult;

  flightFrom_details?: { name: string; iata_code: string };
  flightTo_details?: { name: string; iata_code: string };
  airline_details?: { name: string; iata_code: string };
}

export interface CreateTripStopPayload {
  tripId: string;
  activityType: ActivityType;
  activityData: ActivityDataUnion;
}

export const supportsContactFields = (activityType: ActivityType): boolean => {
  return activityType !== 'Flight';
};

export type DelayCategory =
  | 'LESS_THAN_30_MINUTES'
  | 'BETWEEN_30_AND_60_MINUTES'
  | 'BETWEEN_60_AND_120_MINUTES'
  | 'OVER_120_MINUTES_OR_CANCELLED';

export interface FlightDelayPrediction {
  probability: number;
  result: DelayCategory;
  label: string;
}

export interface FlightDelayPredictionResponse {
  predictions: FlightDelayPrediction[];
}

export interface DelayChartData {
  label: string;
  probability: number;
  category: DelayCategory;
}
